import type { MetaDescriptor, MetaFunction } from 'react-router'

/**
 * Resolves the request origin from the root route's loader data, for building
 * absolute URLs (canonical, og:image, JSON-LD @ids) inside a `meta` export.
 * Falls back to the production domain when loader data isn't available.
 */
export function getMetaOrigin(args: Parameters<MetaFunction>[0]): string {
	const rootMatch = args.matches.find((m) => m.id === 'root')
	return (
		(rootMatch?.data as { requestInfo?: { origin?: string } } | undefined)?.requestInfo?.origin ??
		'https://www.gradeguide.co.uk'
	)
}

/**
 * Merges child route meta with parent route meta, with child values taking
 * priority for any matching keys (title, name, property, link rel).
 *
 * Based on https://gist.github.com/ryanflorence/ec1849c6d690cfbffcb408ecd633e069
 */
export function mergeMeta(leafMetaFn: (arg: Parameters<MetaFunction>[0]) => MetaDescriptor[]): MetaFunction {
	return (arg) => {
		const leafMeta = leafMetaFn(arg)

		return arg.matches.reduceRight((acc, match) => {
			for (const parentMeta of match.meta) {
				const index = acc.findIndex(
					(meta) =>
						('name' in meta && 'name' in parentMeta && meta.name === parentMeta.name) ||
						('property' in meta && 'property' in parentMeta && meta.property === parentMeta.property) ||
						('title' in meta && 'title' in parentMeta) ||
						('tagName' in meta &&
							'tagName' in parentMeta &&
							meta.tagName === parentMeta.tagName &&
							'rel' in meta &&
							'rel' in parentMeta &&
							(meta as Record<string, unknown>).rel === (parentMeta as Record<string, unknown>).rel),
				)
				if (index === -1) acc.push(parentMeta)
			}
			return acc
		}, leafMeta)
	}
}
